import * as React from "react"
import { useMemo } from "react"
import { Box, Typography, Skeleton } from "@mui/material"
import { TableSeeker, IconSeeker } from "@ui-seeker/core"

import { tableData, totalCount, isLoadingTable, groupByField } from "../@signals"
import { searchTextColumn, productIdColumn, skuIdColumn, eanColumn, rawTitleColumn, rawSubtitleColumn, rawSupertitleColumn, rawVariantColumn, skuNameColumn, mappingStatusColumn, lastSeenFilterColumn, newProductFilterColumn, siteCountryCodeColumn, siteColumn, brandColumn, brandStatusFilterColumn, activityColumn, rawProductColumn, mappedSkuColumn, actionsColumn } from "./columns"
import EditMappingDrawer from "../EditMappingDrawer"
import ImageComparisonModal from "../ImageComparisonModal"
import ProductsReviewTableToolbar from "./CustomToolbar"


function getLastSeenCategory(dateString) {
    if (!dateString) return 'Older'
    
    const date = new Date(dateString)
    const now = new Date()
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays <= 7) return 'Last 7 days'
    if (diffDays <= 30) return 'Last 30 days'
    return 'Older'
}

function getNewProductStatus(dateString) {
    if (!dateString) return 'Not new'
    
    const date = new Date(dateString)
    const now = new Date()
    const diffHours = (now - date) / (1000 * 60 * 60)
    const diffDays = diffHours / 24
    
    if (diffHours <= 24) return 'New (24h)'
    if (diffDays <= 7) return 'New (7 days)'
    return 'Not new'
}


export default function ProductsReviewTable() {
    const data = tableData.value
    const total = totalCount.value
    const isLoading = isLoadingTable.value

    const groupField = groupByField.value
    const isGrouped = !!groupField

    // Helper to build a table row from an item
    function buildRow(item) {
        const searchParts = [
            item.raw?.product_id,
            item.raw?.ean,
            item.raw?.reference,
            item.raw?.title,
            item.raw?.subtitle,
            item.raw?.supertitle,
            item.raw?.variant_raw,
            item.raw?.brand,
            item.raw?.site,
            item.sku?.id_sku,
            item.sku?.name
        ].filter(Boolean)

        return {
            search_text: searchParts.join(' '),
            product_id: item.raw?.product_id || '',
            id_sku: item.sku?.id_sku || '',
            ean: item.raw?.ean || '',
            raw_title: item.raw?.title || '',
            raw_subtitle: item.raw?.subtitle || '',
            raw_supertitle: item.raw?.supertitle || '',
            raw_variant: item.raw?.variant_raw || '',
            sku_name: item.sku?.name || '',
            mapping_status: item.sku ? 'Mapped' : 'Unmapped',
            last_seen_filter: getLastSeenCategory(item.end_date),
            new_product_status: getNewProductStatus(item.start_date),
            site: item.raw?.site || '-',
            siteCountryCode: item.raw?.countryCode || '',
            brand: item.raw?.brand || '-',
            brand_status: item.raw?.id_brand ? 'Normalized' : 'Not normalized',
            activity: {
                start_date: item.start_date,
                end_date: item.end_date
            },
            raw: item.raw,
            sku: item.sku
        }
    }

    // Transform data for TableSeeker - flatten the structure
    const tableRows = useMemo(() => {
        if (!isGrouped) return data.map(buildRow)

        // Group items by the selected field
        const groups = new Map()
        const ungrouped = []

        data.forEach(item => {
            const key = item.raw?.[groupField]
            if (!key) {
                ungrouped.push(item)
                return
            }
            if (!groups.has(key)) groups.set(key, [])
            groups.get(key).push(item)
        })

        const rows = []

        groups.forEach((items) => {
            if (items.length === 1) {
                rows.push(buildRow(items[0]))
                return
            }

            // Calculate group status
            const mappedItems = items.filter(i => i.sku)
            const unmappedItems = items.filter(i => !i.sku)
            const uniqueSkuIds = [...new Set(mappedItems.map(i => i.sku.id_sku))]

            let groupStatus = 'consistent'
            let mappedSkuId = null
            if (mappedItems.length === 0) {
                groupStatus = 'consistent' // all unmapped
            } else if (unmappedItems.length > 0 && uniqueSkuIds.length === 1) {
                groupStatus = 'partial'
                mappedSkuId = uniqueSkuIds[0]
            } else if (uniqueSkuIds.length > 1) {
                groupStatus = 'inconsistent'
            } else {
                mappedSkuId = uniqueSkuIds[0]
            }

            // Use first item as representative
            const representative = items[0]
            const groupedSizes = [...new Set(items.map(i => i.raw?.variant_raw).filter(Boolean))]

            // Build row with group metadata
            const row = buildRow(representative)
            row.raw = {
                ...representative.raw,
                _groupedProducts: items,
                _productCount: items.length,
                _groupedSizes: groupedSizes,
                _groupStatus: groupStatus,
                _mappedSkuId: mappedSkuId,
                _unmappedProducts: unmappedItems,
                _groupField: groupField
            }
            // Add all sizes and references to search text
            row.search_text += ' ' + groupedSizes.join(' ')
            rows.push(row)
        })

        // Add ungrouped items
        ungrouped.forEach(item => rows.push(buildRow(item)))

        return rows
    }, [data, isGrouped, groupField])

    if (isLoading) {
        return <Skeleton variant="rectangular" height={500} sx={{ mt: 2 }} />
    }

    if (data.length === 0 && total === 0) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                py: 8,
                color: '#9ca3af',
                backgroundColor: '#fff',
                borderRadius: 1,
                border: '1px solid #e5e7eb'
            }}>
                <IconSeeker icon="magnifying-glass" sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="body1">
                    Select filters and click Search to view products
                </Typography>
            </Box>
        )
    }

    return (
        <>
        <TableSeeker
            data={tableRows}
            title="Products Review"
            columns={[
                searchTextColumn(),
                productIdColumn(),
                skuIdColumn(),
                eanColumn(),
                rawTitleColumn(),
                rawSubtitleColumn(),
                rawSupertitleColumn(),
                rawVariantColumn(),
                skuNameColumn(),
                mappingStatusColumn(),
                lastSeenFilterColumn(),
                newProductFilterColumn(),
                siteCountryCodeColumn(),
                siteColumn(),
                brandColumn(),
                brandStatusFilterColumn(),
                activityColumn(),
                rawProductColumn(),
                mappedSkuColumn(),
                actionsColumn()
            ]}
            options={{
                selectableRows: 'multiple',
                customToolbarSelect: (selectedData, setSelectedRows) => (
                    <ProductsReviewTableToolbar
                        selectedData={selectedData}
                        setSelectedRows={setSelectedRows}
                    />
                ),
                sortOrder: {
                    name: 'activity',
                    direction: 'desc'
                },
                rowsPerPage: 100,
                rowsPerPageOptions: [10, 20, 50, 100, 200]
            }}
            textLabels={{
                body: {
                    noMatch: 'No products found with the selected filters.'
                }
            }}
        />
        <EditMappingDrawer />
        <ImageComparisonModal />
    </>
    )
}
